import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { school, hospital, pos } from '../services/api';
import { useStore } from '../store';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

// ── SHARED COMPONENTS ─────────────────────────────────────────
const Card = ({ title, children, color='#6366f1', style={} }) => (
  <div style={{
    background: '#12121f', border: '1px solid #1e1e3a',
    borderRadius: 14, padding: 20, ...style
  }}>
    {title && <div style={{
      fontSize: 12, fontWeight: 600, color: '#555',
      marginBottom: 14, textTransform: 'uppercase', letterSpacing: 1
    }}>{title}</div>}
    {children}
  </div>
);

const Stat = ({ label, value, color='#6366f1', icon, sub }) => (
  <div style={{
    background: '#12121f', border: '1px solid #1e1e3a',
    borderRadius: 12, padding: '18px 16px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  }}>
    <div>
      <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: '#fff' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color, marginTop: 4 }}>{sub}</div>}
    </div>
    {icon && (
      <div style={{
        width: 42, height: 42, borderRadius: 12,
        background: color + '22', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: 20, color
      }}>{icon}</div>
    )}
  </div>
);

const Input = ({ label, value, onChange, type='text', placeholder='' }) => (
  <div>
    <label style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 4 }}>{label}</label>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder}
      style={{
        width: '100%', background: '#1a1a2e', border: '1px solid #2a2a4a',
        borderRadius: 8, padding: '9px 12px', color: '#fff',
        fontSize: 13, outline: 'none', boxSizing: 'border-box'
      }} />
  </div>
);

const Btn = ({ children, onClick, color='#6366f1', loading, small, style={} }) => (
  <button onClick={onClick} disabled={loading} style={{
    padding: small ? '7px 14px' : '10px 20px',
    background: loading ? '#333' : color,
    border: 'none', borderRadius: 8, color: '#fff',
    fontSize: small ? 12 : 13, fontWeight: 600,
    cursor: loading ? 'not-allowed' : 'pointer', ...style
  }}>{loading ? '...' : children}</button>
);

const Badge = ({ text, color='#6366f1' }) => (
  <span style={{
    background: color + '22', color,
    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600
  }}>{text}</span>
);

const Table = ({ headers, rows, emptyMsg='No records' }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>{headers.map(h => (
          <th key={h} style={{
            padding: '9px 12px', textAlign: 'left', fontSize: 11,
            color: '#555', fontWeight: 600, letterSpacing: 1,
            textTransform: 'uppercase', borderBottom: '1px solid #1e1e3a'
          }}>{h}</th>
        ))}</tr>
      </thead>
      <tbody>
        {rows.length === 0
          ? <tr><td colSpan={headers.length} style={{
              padding: 28, textAlign: 'center', color: '#555', fontSize: 13
            }}>{emptyMsg}</td></tr>
          : rows.map((row, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #12121f' }}>
            {row.map((cell, j) => (
              <td key={j} style={{ padding: '10px 12px', fontSize: 13, color: '#ccc' }}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const OfflineBadge = ({ data }) =>
  data?.offline ? (
    <span style={{
      background: '#f59e0b22', color: '#f59e0b',
      padding: '3px 10px', borderRadius: 20, fontSize: 11, marginLeft: 8
    }}>Offline</span>
  ) : null;

// ══════════════════════════════════════════════════════════
// SCHOOL DASHBOARD
// ══════════════════════════════════════════════════════════
export function SchoolDashboard() {
  const { setScreen } = useStore();
  const [data, setData] = useState(null);
  useEffect(() => { school.dashboard().then(setData).catch(() => {}); }, []);

  return (
    <div style={{ padding: 24, color: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>School Dashboard</div>
        <OfflineBadge data={data} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
        <Stat label="Total Students" value={data?.total_students||0} icon='◉' color='#6366f1' />
        <Stat label="Staff"          value={data?.total_staff||0}    icon='◎' color='#8b5cf6' />
        <Stat label="Present Today"  value={data?.present_today||0}  icon='⬡' color='#10b981' />
        <Stat label="Pending Fees"   value={data?.pending_fees||0}   icon='◈' color='#f59e0b' />
      </div>

      {data?.ai_summary && (
        <Card title="AI Summary" style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, color: '#ccc', lineHeight: 1.7 }}>{data.ai_summary}</div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
        {[
          { label: 'Enroll Student',  screen: 'students',   color: '#6366f1', icon: '◉' },
          { label: 'Mark Attendance', screen: 'attendance', color: '#10b981', icon: '◫' },
          { label: 'Collect Fee',     screen: 'fees',       color: '#f59e0b', icon: '◎' },
          { label: 'Create Exam',     screen: 'exams',      color: '#ec4899', icon: '◬' },
          { label: 'View Reports',    screen: 'reportcards',color: '#8b5cf6', icon: '⬡' },
          { label: 'Staff List',      screen: 'staff',      color: '#f97316', icon: '◈' },
        ].map(a => (
          <button key={a.screen} onClick={() => setScreen(a.screen)} style={{
            background: '#12121f', border: `1px solid ${a.color}33`,
            borderRadius: 12, padding: '18px',
            display: 'flex', alignItems: 'center', gap: 12,
            cursor: 'pointer', textAlign: 'left'
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: a.color + '22', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: 18, color: a.color
            }}>{a.icon}</div>
            <span style={{ fontSize: 13, color: '#ccc', fontWeight: 600 }}>{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// STUDENTS MANAGEMENT
// ══════════════════════════════════════════════════════════
export function StudentsScreen() {
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name:'', class_name:'', section:'A',
    parent_phone:'', parent_email:'', dob:''
  });

  const load = () => school.students(classFilter||null)
    .then(r => setStudents(r.students||[])).catch(()=>{});

  useEffect(() => { load(); }, [classFilter]);

  const filtered = students.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.student_id?.includes(search)
  );

  const addStudent = async () => {
    if (!form.name || !form.class_name || !form.parent_phone) {
      toast.error('Name, class and parent phone required'); return;
    }
    setLoading(true);
    try {
      const r = await school.addStudent(form);
      toast.success(`Enrolled: ${r.student_id}`);
      setShowAdd(false);
      setForm({name:'',class_name:'',section:'A',parent_phone:'',parent_email:'',dob:''});
      load();
    } catch(e) { toast.error(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ padding: 24, color: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>Students ({filtered.length})</div>
        <Btn onClick={() => setShowAdd(!showAdd)} color='#6366f1'>
          {showAdd ? 'Cancel' : '+ Enroll Student'}
        </Btn>
      </div>

      {showAdd && (
        <Card title="Enroll New Student" style={{ marginBottom: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            <Input label="Full Name *" value={form.name}
              onChange={e => setForm({...form, name:e.target.value})} />
            <Input label="Class *" value={form.class_name}
              onChange={e => setForm({...form, class_name:e.target.value})} />
            <Input label="Section" value={form.section}
              onChange={e => setForm({...form, section:e.target.value})} />
            <Input label="Parent Phone *" type="tel" value={form.parent_phone}
              onChange={e => setForm({...form, parent_phone:e.target.value})} />
            <Input label="Parent Email" type="email" value={form.parent_email}
              onChange={e => setForm({...form, parent_email:e.target.value})} />
            <Input label="Date of Birth" type="date" value={form.dob}
              onChange={e => setForm({...form, dob:e.target.value})} />
          </div>
          <div style={{ marginTop: 16 }}>
            <Btn onClick={addStudent} loading={loading} color='#6366f1'>Enroll Student</Btn>
          </div>
        </Card>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <input placeholder="Search by name or ID..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, background: '#12121f', border: '1px solid #1e1e3a',
            borderRadius: 10, padding: '10px 14px', color: '#fff',
            fontSize: 13, outline: 'none'
          }} />
        <input placeholder="Filter by class..."
          value={classFilter} onChange={e => setClassFilter(e.target.value)}
          style={{
            width: 150, background: '#12121f', border: '1px solid #1e1e3a',
            borderRadius: 10, padding: '10px 14px', color: '#fff',
            fontSize: 13, outline: 'none'
          }} />
      </div>

      <Card>
        <Table
          headers={['Student ID','Name','Class','Section','Parent Phone','Status']}
          rows={filtered.map(s => [
            <span style={{ color: '#6366f1', fontWeight: 600 }}>{s.student_id}</span>,
            s.name, s.class_name, s.section||'A',
            s.parent_phone||'—',
            <Badge text={s.status||'active'} color='#10b981' />
          ])}
          emptyMsg="No students found"
        />
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ATTENDANCE
// ══════════════════════════════════════════════════════════
export function AttendanceScreen() {
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [tab, setTab] = useState('mark');

  useEffect(() => {
    school.students().then(r => {
      setStudents(r.students||[]);
      setSelected(new Set((r.students||[]).map(s => s.id?.toString() || s.student_id)));
    }).catch(()=>{});
  }, []);

  const markAttendance = async (status) => {
    if (selected.size === 0) { toast.error('Select students'); return; }
    setLoading(true);
    try {
      const r = await school.markAttendance({
        student_ids: [...selected],
        date, status
      });
      toast.success(`Marked ${r.marked} students as ${status}`);
      if (r.absent_count > 0)
        toast.success(`${r.absent_count} parent notifications queued`);
    } catch(e) { toast.error(e.message); }
    setLoading(false);
  };

  const loadReport = async () => {
    const r = await school.attReport().catch(() => null);
    setReport(r?.report || []);
    setTab('report');
  };

  const toggleStudent = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  return (
    <div style={{ padding: 24, color: '#fff' }}>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 20 }}>Attendance</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['mark','report'].map(t => (
          <button key={t} onClick={() => t === 'report' ? loadReport() : setTab(t)} style={{
            padding: '8px 18px', background: tab === t ? '#10b981' : '#12121f',
            border: `1px solid ${tab === t ? '#10b981' : '#1e1e3a'}`,
            borderRadius: 10, color: tab === t ? '#fff' : '#666',
            fontSize: 13, cursor: 'pointer', textTransform: 'capitalize'
          }}>{t}</button>
        ))}
      </div>

      {tab === 'mark' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
            <Input label="Date" type="date" value={date}
              onChange={e => setDate(e.target.value)} />
            <div style={{ paddingTop: 18, display: 'flex', gap: 8 }}>
              <Btn onClick={() => markAttendance('present')} loading={loading} color='#10b981'>
                Mark Present ({selected.size})
              </Btn>
              <Btn onClick={() => markAttendance('absent')} loading={loading} color='#ef4444'>
                Mark Absent
              </Btn>
              <Btn onClick={() => setSelected(new Set(students.map(s => s.id?.toString()||s.student_id)))}
                color='#1a1a2e' style={{ border: '1px solid #333' }}>
                Select All
              </Btn>
            </div>
          </div>

          <Card>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {students.map(s => {
                const id = s.id?.toString() || s.student_id;
                const sel = selected.has(id);
                return (
                  <button key={id} onClick={() => toggleStudent(id)} style={{
                    padding: '8px 14px',
                    background: sel ? '#10b98122' : '#1a1a2e',
                    border: `1px solid ${sel ? '#10b981' : '#2a2a4a'}`,
                    borderRadius: 8, color: sel ? '#10b981' : '#666',
                    fontSize: 12, cursor: 'pointer'
                  }}>
                    {sel ? '✓ ' : ''}{s.name} ({s.class_name})
                  </button>
                );
              })}
              {students.length === 0 && (
                <div style={{ color: '#555', fontSize: 13 }}>No students enrolled yet</div>
              )}
            </div>
          </Card>
        </>
      )}

      {tab === 'report' && report && (
        <Card title="Attendance Report">
          <Table
            headers={['Name','Class','Present','Absent','Total','%']}
            rows={(report||[]).map(r => [
              r.name, r.class_name||'—',
              r.present_days||0, r.absent_days||0, r.total_days||0,
              <span style={{ color: parseFloat(r.attendance_pct||0) >= 75 ? '#10b981' : '#ef4444' }}>
                {r.attendance_pct||0}%
              </span>
            ])}
            emptyMsg="No attendance records yet"
          />
        </Card>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// FEE COLLECTION
// ══════════════════════════════════════════════════════════
export function FeesScreen() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ student_id:'', fee_type:'tuition', amount_txc:'', month:'' });
  const [result, setResult] = useState(null);

  useEffect(() => {
    school.pendingFees().then(r => setPending(r.pending_fees||[])).catch(()=>{});
  }, []);

  const collect = async () => {
    if (!form.student_id || !form.amount_txc) {
      toast.error('Student ID and amount required'); return;
    }
    setLoading(true);
    try {
      const r = await school.collectFee({
        ...form, amount_txc: parseFloat(form.amount_txc)
      });
      setResult(r);
      toast.success(`Receipt: ${r.receipt_no}`);
      setPending(p => p.filter(x => x.student_id !== form.student_id));
    } catch(e) { toast.error(e.message); }
    setLoading(false);
  };

  const feeTypes = ['tuition','transport','library','lab','sports','exam','hostel','other'];

  return (
    <div style={{ padding: 24, color: '#fff' }}>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 20 }}>Fee Collection</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <Card title="Collect Fee">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Input label="Student ID" value={form.student_id}
              onChange={e => setForm({...form, student_id:e.target.value})} />
            <div>
              <label style={{ fontSize:11, color:'#555', display:'block', marginBottom:4 }}>Fee Type</label>
              <select value={form.fee_type} onChange={e => setForm({...form, fee_type:e.target.value})}
                style={{
                  width:'100%', background:'#1a1a2e', border:'1px solid #2a2a4a',
                  borderRadius:8, padding:'9px 12px', color:'#fff', fontSize:13, outline:'none'
                }}>
                {feeTypes.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
              </select>
            </div>
            <Input label="Amount (TXC)" type="number" value={form.amount_txc}
              onChange={e => setForm({...form, amount_txc:e.target.value})} />
            <Input label="Month (e.g. 2026-04)" value={form.month}
              onChange={e => setForm({...form, month:e.target.value})} />
            <Btn onClick={collect} loading={loading} color='#f59e0b'>
              Collect via TXC Wallet
            </Btn>
          </div>

          {result && (
            <div style={{
              marginTop: 16, background: '#0f1535',
              border: '1px solid #6366f144',
              borderRadius: 10, padding: 16
            }}>
              <div style={{ fontSize: 12, color: '#6366f1', marginBottom: 8 }}>Receipt Generated</div>
              <div style={{ fontSize: 13, color: '#ccc' }}>
                Receipt: {result.receipt_no}<br/>
                Student: {result.student}<br/>
                Amount: {result.amount_txc} TXC<br/>
                Status: <Badge text={result.status} color={result.status==='paid'?'#10b981':'#f59e0b'} />
              </div>
            </div>
          )}
        </Card>

        <Card title={`Pending Fees (${pending.length})`}>
          <Table
            headers={['Student','Class','Type','Amount','Month']}
            rows={pending.map(f => [
              f.name, f.class_name,
              f.fee_type,
              <span style={{ color: '#f59e0b', fontWeight: 700 }}>{f.amount_txc} TXC</span>,
              f.month||'—'
            ])}
            emptyMsg="No pending fees"
          />
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// EXAMS & RESULTS
// ══════════════════════════════════════════════════════════
export function ExamsScreen() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('list');
  const [examForm, setExamForm] = useState({
    name:'', class_name:'', subject:'', exam_date:'', max_marks:100, pass_marks:35
  });
  const [resultForm, setResultForm] = useState({ student_id:'', exam_id:'', marks:'', remarks:'' });
  const [resultData, setResultData] = useState(null);

  const load = () => school.exams().then(r => setExams(r.exams||[])).catch(()=>{});
  useEffect(() => { load(); }, []);

  const createExam = async () => {
    setLoading(true);
    try {
      await school.createExam(examForm);
      toast.success('Exam created!');
      load();
      setTab('list');
    } catch(e) { toast.error(e.message); }
    setLoading(false);
  };

  const enterResult = async () => {
    setLoading(true);
    try {
      const r = await school.enterResult({
        ...resultForm, marks: parseFloat(resultForm.marks)
      });
      setResultData(r);
      toast.success(`Grade: ${r.grade} — ${r.percentage}%`);
    } catch(e) { toast.error(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ padding: 24, color: '#fff' }}>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 20 }}>Exams & Results</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['list','create','result'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 18px',
            background: tab === t ? '#ec4899' : '#12121f',
            border: `1px solid ${tab === t ? '#ec4899' : '#1e1e3a'}`,
            borderRadius: 10, color: tab === t ? '#fff' : '#666',
            fontSize: 13, cursor: 'pointer', textTransform: 'capitalize'
          }}>{t === 'result' ? 'Enter Result' : t}</button>
        ))}
      </div>

      {tab === 'list' && (
        <Card title={`Exams (${exams.length})`}>
          <Table
            headers={['Name','Class','Subject','Date','Max Marks','Pass Marks']}
            rows={exams.map(e => [
              e.name, e.class_name, e.subject||'—',
              e.exam_date||'—', e.max_marks, e.pass_marks
            ])}
            emptyMsg="No exams created yet"
          />
        </Card>
      )}

      {tab === 'create' && (
        <Card title="Create Exam">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            <Input label="Exam Name" value={examForm.name}
              onChange={e => setExamForm({...examForm, name:e.target.value})} />
            <Input label="Class" value={examForm.class_name}
              onChange={e => setExamForm({...examForm, class_name:e.target.value})} />
            <Input label="Subject" value={examForm.subject}
              onChange={e => setExamForm({...examForm, subject:e.target.value})} />
            <Input label="Exam Date" type="date" value={examForm.exam_date}
              onChange={e => setExamForm({...examForm, exam_date:e.target.value})} />
            <Input label="Max Marks" type="number" value={examForm.max_marks}
              onChange={e => setExamForm({...examForm, max_marks:parseFloat(e.target.value)})} />
            <Input label="Pass Marks" type="number" value={examForm.pass_marks}
              onChange={e => setExamForm({...examForm, pass_marks:parseFloat(e.target.value)})} />
          </div>
          <Btn onClick={createExam} loading={loading} color='#ec4899' style={{ marginTop: 16 }}>
            Create Exam
          </Btn>
        </Card>
      )}

      {tab === 'result' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <Card title="Enter Result">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Input label="Student ID" value={resultForm.student_id}
                onChange={e => setResultForm({...resultForm, student_id:e.target.value})} />
              <div>
                <label style={{ fontSize:11, color:'#555', display:'block', marginBottom:4 }}>Exam</label>
                <select value={resultForm.exam_id}
                  onChange={e => setResultForm({...resultForm, exam_id:e.target.value})}
                  style={{
                    width:'100%', background:'#1a1a2e', border:'1px solid #2a2a4a',
                    borderRadius:8, padding:'9px 12px', color:'#fff', fontSize:13, outline:'none'
                  }}>
                  <option value="">Select exam...</option>
                  {exams.map(ex => (
                    <option key={ex.id} value={ex.id}>{ex.name} — {ex.subject}</option>
                  ))}
                </select>
              </div>
              <Input label="Marks Obtained" type="number" value={resultForm.marks}
                onChange={e => setResultForm({...resultForm, marks:e.target.value})} />
              <Input label="Remarks" value={resultForm.remarks}
                onChange={e => setResultForm({...resultForm, remarks:e.target.value})} />
              <Btn onClick={enterResult} loading={loading} color='#6366f1'>
                Save Result
              </Btn>
            </div>
          </Card>

          {resultData && (
            <Card title="Result">
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{
                  fontSize: 72, fontWeight: 900,
                  color: resultData.grade === 'A+' || resultData.grade === 'A' ? '#10b981' :
                         resultData.grade === 'B+' || resultData.grade === 'B' ? '#6366f1' :
                         resultData.grade === 'C' ? '#f59e0b' :
                         resultData.grade === 'D' ? '#f97316' : '#ef4444'
                }}>{resultData.grade}</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {resultData.marks}/{resultData.max_marks}
                </div>
                <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>
                  {resultData.percentage}%
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Badge
                  text={resultData.passed ? 'PASSED' : 'FAILED'}
                  color={resultData.passed ? '#10b981' : '#ef4444'}
                />
              </div>
              {resultData.qw_hash && (
                <div style={{ marginTop: 12, fontSize: 11, color: '#555', textAlign: 'center' }}>
                  ◈ Verified on QuantumWeave
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// HOSPITAL DASHBOARD
// ══════════════════════════════════════════════════════════
export function HospitalDashboard() {
  const { setScreen } = useStore();
  const [data, setData] = useState(null);
  useEffect(() => { hospital.dashboard().then(setData).catch(()=>{}); }, []);

  return (
    <div style={{ padding: 24, color: '#fff' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <div style={{ fontSize:22, fontWeight:800 }}>Hospital Dashboard</div>
        <OfflineBadge data={data} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:24 }}>
        <Stat label="Total Patients"     value={data?.total_patients||0}     icon='◉' color='#ef4444' />
        <Stat label="Appointments Today" value={data?.total_appointments||0} icon='◫' color='#6366f1' />
        <Stat label="Doctors"            value={data?.total_doctors||0}      icon='◎' color='#10b981' />
        <Stat label="Revenue"            value={`${data?.revenue_txc||0} TXC`} icon='⬡' color='#f59e0b' />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
        {[
          { label:'New Patient',     screen:'patients',     color:'#ef4444', icon:'◉' },
          { label:'Book Appointment',screen:'appointments', color:'#6366f1', icon:'◫' },
          { label:'Doctors',         screen:'doctors',      color:'#10b981', icon:'◎' },
          { label:'Billing',         screen:'billing',      color:'#f59e0b', icon:'◈' },
        ].map(a => (
          <button key={a.screen} onClick={() => setScreen(a.screen)} style={{
            background:'#12121f', border:`1px solid ${a.color}33`,
            borderRadius:12, padding:'18px',
            display:'flex', alignItems:'center', gap:12,
            cursor:'pointer', textAlign:'left'
          }}>
            <div style={{
              width:40, height:40, borderRadius:10,
              background:a.color+'22', display:'flex',
              alignItems:'center', justifyContent:'center',
              fontSize:18, color:a.color
            }}>{a.icon}</div>
            <span style={{ fontSize:13, color:'#ccc', fontWeight:600 }}>{a.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PATIENTS
// ══════════════════════════════════════════════════════════
export function PatientsScreen() {
  const [patients, setPatients] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name:'', phone:'', dob:'', blood_group:'', address:''
  });

  const load = () => hospital.patients().then(r => setPatients(r.patients||[])).catch(()=>{});
  useEffect(() => { load(); }, []);

  const addPatient = async () => {
    setLoading(true);
    try {
      const r = await hospital.addPatient(form);
      toast.success(`Patient registered: ${r.patient_id}`);
      setShowAdd(false);
      load();
    } catch(e) { toast.error(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ padding:24, color:'#fff' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:22, fontWeight:800 }}>Patients ({patients.length})</div>
        <Btn onClick={() => setShowAdd(!showAdd)} color='#ef4444'>
          {showAdd ? 'Cancel' : '+ New Patient'}
        </Btn>
      </div>

      {showAdd && (
        <Card title="Register Patient" style={{ marginBottom:20 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
            <Input label="Full Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
            <Input label="Phone" type="tel" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} />
            <Input label="Date of Birth" type="date" value={form.dob} onChange={e=>setForm({...form,dob:e.target.value})} />
            <Input label="Blood Group" value={form.blood_group} onChange={e=>setForm({...form,blood_group:e.target.value})} />
            <Input label="Address" value={form.address} onChange={e=>setForm({...form,address:e.target.value})} />
          </div>
          <Btn onClick={addPatient} loading={loading} color='#ef4444' style={{marginTop:16}}>
            Register Patient
          </Btn>
        </Card>
      )}

      <Card>
        <Table
          headers={['Patient ID','Name','Phone','Blood Group','Registered']}
          rows={patients.map(p => [
            <span style={{color:'#ef4444',fontWeight:600}}>{p.patient_id}</span>,
            p.name, p.phone||'—', p.blood_group||'—',
            p._created ? new Date(p._created).toLocaleDateString() : '—'
          ])}
          emptyMsg="No patients registered"
        />
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// DOCTORS
// ══════════════════════════════════════════════════════════
export function DoctorsScreen() {
  const [doctors, setDoctors] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name:'', specialization:'', qualification:'', consultation_fee_txc:''
  });

  const load = () => hospital.doctors().then(r => setDoctors(r.doctors||[])).catch(()=>{});
  useEffect(() => { load(); }, []);

  const addDoctor = async () => {
    setLoading(true);
    try {
      await hospital.addDoctor({...form, consultation_fee_txc: parseFloat(form.consultation_fee_txc||0)});
      toast.success('Doctor registered!');
      setShowAdd(false);
      load();
    } catch(e) { toast.error(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ padding:24, color:'#fff' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div style={{ fontSize:22, fontWeight:800 }}>Doctors ({doctors.length})</div>
        <Btn onClick={() => setShowAdd(!showAdd)} color='#10b981'>
          {showAdd ? 'Cancel' : '+ Add Doctor'}
        </Btn>
      </div>

      {showAdd && (
        <Card title="Register Doctor" style={{ marginBottom:20 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12 }}>
            <Input label="Full Name" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
            <Input label="Specialization" value={form.specialization} onChange={e=>setForm({...form,specialization:e.target.value})} />
            <Input label="Qualification" value={form.qualification} onChange={e=>setForm({...form,qualification:e.target.value})} />
            <Input label="Consultation Fee (TXC)" type="number" value={form.consultation_fee_txc} onChange={e=>setForm({...form,consultation_fee_txc:e.target.value})} />
          </div>
          <Btn onClick={addDoctor} loading={loading} color='#10b981' style={{marginTop:16}}>
            Register Doctor
          </Btn>
        </Card>
      )}

      <Card>
        <Table
          headers={['Name','Specialization','Qualification','Fee (TXC)','Status']}
          rows={doctors.map(d => [
            d.name, d.specialization||'—', d.qualification||'—',
            d.consultation_fee_txc||0,
            <Badge text={d.available?'Available':'Busy'} color={d.available?'#10b981':'#f59e0b'} />
          ])}
          emptyMsg="No doctors registered"
        />
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// POS — POINT OF SALE
// ══════════════════════════════════════════════════════════
export function POSSellScreen() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    pos.products().then(r => setProducts(r.products||[])).catch(()=>{});
  }, []);

  const addToCart = (product) => {
    const existing = cart.find(c => c.id === product.id);
    if (existing) {
      setCart(cart.map(c => c.id === product.id ? {...c, qty: c.qty+1} : c));
    } else {
      setCart([...cart, {...product, qty: 1}]);
    }
  };

  const removeFromCart = (id) => setCart(cart.filter(c => c.id !== id));

  const total = cart.reduce((sum, c) => sum + (parseFloat(c.price_txc||0) * c.qty), 0);

  const checkout = async () => {
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    setLoading(true);
    try {
      const r = await pos.createOrder(cart, total);
      setReceipt({ ...r, items: cart, total, date: new Date().toLocaleString() });
      setCart([]);
      toast.success('Sale completed!');
    } catch(e) { toast.error(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ padding:24, color:'#fff' }}>
      <div style={{ fontSize:22, fontWeight:800, marginBottom:20 }}>Point of Sale</div>

      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:20 }}>
        {/* Products */}
        <div>
          <div style={{ fontSize:13, color:'#555', marginBottom:14 }}>
            Products ({products.length})
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
            {products.length === 0 ? (
              <div style={{ gridColumn:'1/-1', color:'#555', fontSize:13, padding:20 }}>
                No products. Add products in the Products screen.
              </div>
            ) : products.map(p => (
              <button key={p.id} onClick={() => addToCart(p)} style={{
                background:'#12121f', border:'1px solid #1e1e3a',
                borderRadius:12, padding:14,
                cursor:'pointer', textAlign:'left'
              }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#fff', marginBottom:4 }}>{p.name}</div>
                <div style={{ fontSize:15, fontWeight:800, color:'#10b981' }}>
                  {p.price_txc||0} TXC
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Cart */}
        <div>
          <Card title="Cart">
            {cart.length === 0 ? (
              <div style={{ color:'#555', fontSize:13, padding:'20px 0', textAlign:'center' }}>
                Tap products to add
              </div>
            ) : (
              <>
                {cart.map(item => (
                  <div key={item.id} style={{
                    display:'flex', justifyContent:'space-between',
                    alignItems:'center', marginBottom:10,
                    padding:'8px 0', borderBottom:'1px solid #1e1e3a'
                  }}>
                    <div>
                      <div style={{ fontSize:13, color:'#fff' }}>{item.name}</div>
                      <div style={{ fontSize:11, color:'#555' }}>× {item.qty}</div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontSize:13, color:'#10b981', fontWeight:700 }}>
                        {(parseFloat(item.price_txc||0) * item.qty).toFixed(2)} TXC
                      </span>
                      <button onClick={() => removeFromCart(item.id)} style={{
                        background:'#ef444422', border:'none', borderRadius:6,
                        color:'#ef4444', fontSize:11, padding:'3px 8px', cursor:'pointer'
                      }}>✕</button>
                    </div>
                  </div>
                ))}

                <div style={{
                  marginTop:16, padding:'12px 0',
                  borderTop:'2px solid #6366f1',
                  display:'flex', justifyContent:'space-between'
                }}>
                  <span style={{ fontWeight:700, fontSize:15 }}>TOTAL</span>
                  <span style={{ fontWeight:900, fontSize:18, color:'#10b981' }}>
                    {total.toFixed(4)} TXC
                  </span>
                </div>

                <Btn onClick={checkout} loading={loading} color='#10b981' style={{ width:'100%', marginTop:12 }}>
                  Complete Sale
                </Btn>
              </>
            )}
          </Card>

          {receipt && (
            <Card title="Receipt" style={{ marginTop:16 }}>
              <div style={{ fontSize:12, color:'#888' }}>
                Order: {receipt.order_id}<br/>
                Date: {receipt.date}<br/>
                Total: <span style={{ color:'#10b981', fontWeight:700 }}>{receipt.total?.toFixed(4)} TXC</span>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// REPORT CARDS
// ══════════════════════════════════════════════════════════
export function ReportCardsScreen() {
  const [studentId, setStudentId] = useState('');
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!studentId) { toast.error('Enter student ID'); return; }
    setLoading(true);
    try {
      const r = await school.reportCard(studentId);
      setCard(r);
    } catch(e) { toast.error(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ padding:24, color:'#fff' }}>
      <div style={{ fontSize:22, fontWeight:800, marginBottom:20 }}>Report Cards</div>

      <Card style={{ marginBottom:20 }}>
        <div style={{ display:'flex', gap:12, alignItems:'flex-end' }}>
          <div style={{ flex:1 }}>
            <Input label="Student ID or UUID" value={studentId}
              onChange={e => setStudentId(e.target.value)} />
          </div>
          <Btn onClick={generate} loading={loading} color='#8b5cf6'>
            Generate Report Card
          </Btn>
        </div>
      </Card>

      {card && (
        <Card title="Report Card">
          {card.student && (
            <div style={{ marginBottom:20, padding:16, background:'#0f1535', borderRadius:10 }}>
              <div style={{ fontSize:18, fontWeight:800 }}>{card.student.name}</div>
              <div style={{ fontSize:13, color:'#555', marginTop:4 }}>
                {card.student.student_id} · Class {card.student.class_name}
              </div>
            </div>
          )}

          <Table
            headers={['Subject','Exam','Marks','Max','Grade','QW Verified']}
            rows={(card.results||[]).map(r => [
              r.subject||'—', r.exam_name,
              r.marks, r.max_marks,
              <span style={{
                fontWeight:800,
                color: r.grade==='A+'||r.grade==='A' ? '#10b981' :
                       r.grade==='B+'||r.grade==='B' ? '#6366f1' :
                       r.grade==='C' ? '#f59e0b' : '#ef4444'
              }}>{r.grade}</span>,
              r.qw_hash ? <span style={{color:'#6366f1',fontSize:11}}>◈ Yes</span> : '—'
            ])}
            emptyMsg="No results found"
          />

          {card.overall_percentage > 0 && (
            <div style={{
              marginTop:16, padding:16,
              background:'#0f1535', borderRadius:10,
              display:'flex', justifyContent:'space-between', alignItems:'center'
            }}>
              <div>
                <div style={{ fontSize:13, color:'#555' }}>Overall Result</div>
                <div style={{ fontSize:24, fontWeight:800, marginTop:4 }}>
                  {card.overall_percentage}% —{' '}
                  <span style={{ color:'#6366f1' }}>{card.overall_grade}</span>
                </div>
              </div>
              <div style={{ fontSize:13, color:'#555' }}>
                {card.total_marks} / {card.total_max_marks}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ── COMING SOON ───────────────────────────────────────────────
export function ComingSoon({ title }) {
  return (
    <div style={{ padding:40, color:'#fff', textAlign:'center' }}>
      <div style={{ fontSize:48, marginBottom:16 }}>◈</div>
      <div style={{ fontSize:20, fontWeight:800, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:14, color:'#555' }}>Being built. Coming soon.</div>
    </div>
  );
}
